// apps/web/src/features/clients/useMobileFieldStyle.ts
//
// Mobile-safe typography for the client editors (AC-12).
//
// DANGER: mobile WebKit auto-zooms a focused native field whose own font is
// under 16 px, and that zoom breaks the narrow editor's visual-viewport sizing
// AND leaves the page scrolled sideways. Raising the field font is the fix that
// does NOT require `user-scalable=no` — never disable pinch zoom instead.
//
// WHY pointer coarseness and not width alone: a phone in landscape is wider
// than the 900 px narrow breakpoint but still auto-zooms. `(pointer: coarse)`
// keeps the 16 px rule attached to touch input, where it actually applies.
import { useEffect, useState, type CSSProperties } from 'react';
import { useNarrow } from '../../ui/index.js';

const COARSE_POINTER_QUERY = '(pointer: coarse)';

function readCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(COARSE_POINTER_QUERY).matches;
}

export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(readCoarsePointer);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(COARSE_POINTER_QUERY);
    const on = (): void => setCoarse(mql.matches);
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);
  return coarse;
}

/** No overrides on desktop: those surfaces keep the compact 13 px register. */
const DESKTOP_FIELD_STYLE: CSSProperties = {};
const TOUCH_FIELD_STYLE: CSSProperties = { fontSize: 'var(--fz-16)' };

/**
 * Style to pass to `Input` / `Textarea` / `PhoneInput` inside a client editor.
 * Always an object so call sites stay a plain `style={fieldStyle}`.
 */
export function useMobileFieldStyle(): CSSProperties {
  const narrow = useNarrow();
  const coarse = useCoarsePointer();
  return narrow || coarse ? TOUCH_FIELD_STYLE : DESKTOP_FIELD_STYLE;
}

/** Minimum comfortable touch target, in CSS px (AC-12). */
export const TOUCH_TARGET_PX = 44;

/** Icon-button size that stays compact on desktop and tappable on touch. */
export function useActionSize(): number {
  const narrow = useNarrow();
  const coarse = useCoarsePointer();
  return narrow || coarse ? TOUCH_TARGET_PX : 28;
}

// Same expression the shared `ConfirmModal` uses for its opted-in footer, so
// "44 px minimum" has one definition and it is derived from the control tokens.
const TOUCH_ACTION_STYLE: CSSProperties = { minHeight: 'calc(var(--control-h) + var(--s-4))' };
// No override on desktop — but still an object, so call sites stay a plain
// `style={actionStyle}` under `exactOptionalPropertyTypes`.
const DESKTOP_ACTION_STYLE: CSSProperties = {};

/**
 * Style for a labelled editor action (Save / Cancel). Desktop keeps the shared
 * `--control-h` button height; touch raises it to the 44 px minimum, matching
 * the opt-in `Modal` / `ConfirmModal` footer contract.
 */
export function useTouchActionStyle(): CSSProperties {
  const narrow = useNarrow();
  const coarse = useCoarsePointer();
  return narrow || coarse ? TOUCH_ACTION_STYLE : DESKTOP_ACTION_STYLE;
}

// A shared control that takes its height from `--control-h-sm` and exposes no
// `style` prop (`FilterToggleButton`) can only be raised from OUTSIDE, by
// redefining the token it reads in a scope around it.
//
// DANGER: never redefine a control token FROM ITSELF —
// `--control-h-sm: calc(var(--control-h-sm) + var(--s-4))` is circular and
// compounds on every nested scope. This reads `--control-h`, the same
// expression `TOUCH_ACTION_STYLE` uses, so both paths land on the same 44 px.
const TOUCH_CONTROL_SCOPE_STYLE = {
  '--control-h-sm': 'calc(var(--control-h) + var(--s-4))',
} as CSSProperties;
const DESKTOP_CONTROL_SCOPE_STYLE: CSSProperties = {};

/**
 * Style for the element WRAPPING a shared small control that has no `style`
 * prop of its own. Desktop keeps the compact 28 px control; touch raises it to
 * the same minimum as `useTouchActionStyle`.
 */
export function useTouchControlScopeStyle(): CSSProperties {
  const narrow = useNarrow();
  const coarse = useCoarsePointer();
  return narrow || coarse ? TOUCH_CONTROL_SCOPE_STYLE : DESKTOP_CONTROL_SCOPE_STYLE;
}

// Same problem one token up: `ErrorState` renders its own retry `Button` and
// takes no `style`, so the initial-load error offers a 32 px target on a phone.
// The button reads `--control-h`, which the wrapper redefines.
//
// DANGER: `--control-h: calc(var(--control-h) + var(--s-4))` would be a SELF-
// reference — invalid at computed-value time, and compounding in every nested
// scope if it resolved. `--s-8` is the 32 px spacing step and equals the
// desktop control height, so `+ --s-4` lands on the same 44 px as
// `TOUCH_ACTION_STYLE` while staying token-derived and idempotent.
const TOUCH_CONTROL_HEIGHT_SCOPE_STYLE = {
  '--control-h': 'calc(var(--s-8) + var(--s-4))',
} as CSSProperties;

/**
 * Style for the element WRAPPING a shared block that renders its own default-
 * size control and has no `style` prop (`ErrorState`). Desktop keeps 32 px.
 */
export function useTouchControlHeightScopeStyle(): CSSProperties {
  const narrow = useNarrow();
  const coarse = useCoarsePointer();
  return narrow || coarse ? TOUCH_CONTROL_HEIGHT_SCOPE_STYLE : DESKTOP_CONTROL_SCOPE_STYLE;
}

/**
 * True when the surface is narrow OR the pointer is coarse — the input case the
 * 16 px / 44 px rules above exist for. Callers that opt a shared component into
 * a touch presentation (rather than passing a style) read this.
 */
export function useTouchInput(): boolean {
  const narrow = useNarrow();
  const coarse = useCoarsePointer();
  return narrow || coarse;
}
